import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";

const ICON_OPTIONS = [
  ["solar:home-smile-linear", "Inicio"],
  ["solar:buildings-2-linear", "Edificios"],
  ["solar:buildings-linear", "Empresa"],
  ["solar:users-group-rounded-linear", "Usuarios"],
  ["solar:user-linear", "Usuario"],
  ["solar:shield-user-linear", "Seguridad"],
  ["solar:layers-linear", "Capas"],
  ["solar:settings-linear", "Configuración"],
  ["solar:palette-linear", "Diseño"],
  ["solar:upload-linear", "Carga"],
  ["solar:list-linear", "Lista"],
  ["solar:star-linear", "Favorito"],
  ["solar:widget-linear", "Widget"],
  ["solar:link-linear", "Enlace"],
];

const CARD_COLORS = [
  { background: "#eff6ff", border: "#bfdbfe", accent: "#2563eb" },
  { background: "#f0fdf4", border: "#bbf7d0", accent: "#16a34a" },
  { background: "#fff7ed", border: "#fed7aa", accent: "#ea580c" },
  { background: "#faf5ff", border: "#e9d5ff", accent: "#9333ea" },
  { background: "#fdf2f8", border: "#fbcfe8", accent: "#db2777" },
];

function authHeaders() {
  const token = sessionStorage.getItem("syrakis_token");
  return { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

function updateMap(setConfig, mapName, key, field, value) {
  setConfig((current) => ({
    ...current,
    [mapName]: {
      ...(current[mapName] || {}),
      [key]: { ...(current[mapName]?.[key] || {}), [field]: field === "order" ? Number(value || 0) : field === "visible" ? Boolean(value) : value },
    },
  }));
}

function IconPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const filtered = ICON_OPTIONS.filter(([icon, label]) => `${icon} ${label}`.toLowerCase().includes(query.toLowerCase()));
  const options = filtered.some(([icon]) => icon === value) ? filtered : [[value, "Actual"], ...filtered];
  return (
    <div className="menu-icon-picker d-flex align-items-center gap-8">
      <Icon icon={value || "solar:widget-linear"} className="text-primary-600 text-xl flex-shrink-0" />
      <input className="form-control form-control-sm" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar icono" aria-label="Buscar icono" />
      <select className="form-select form-select-sm" value={value || "solar:widget-linear"} onChange={(event) => onChange(event.target.value)} aria-label="Seleccionar icono">
        {options.map(([icon, label]) => <option value={icon} key={icon}>{label} · {icon}</option>)}
      </select>
    </div>
  );
}

function isCustomKey(key) {
  return String(key).startsWith("custom:");
}

export default function MenuManagementPage() {
  const [features, setFeatures] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState("");
  const selected = useMemo(() => features.find((feature) => feature.id === selectedId) || null, [features, selectedId]);

  async function load() {
    const response = await fetch("/api/menu/features", { credentials: "same-origin", headers: authHeaders() });
    if (!response.ok) throw new Error("No se pudieron cargar las plantillas.");
    const data = await response.json();
    setFeatures(data.features || []);
    if (!selectedId && data.features?.[0]) setSelectedId(data.features[0].id);
    const first = data.features?.find((feature) => feature.id === selectedId) || data.features?.[0];
    if (first) setConfig(copy(first.config || {}));
  }

  useEffect(() => { load().catch((error) => setStatus(error.message)); }, []);

  useEffect(() => {
    const feature = features.find((item) => item.id === selectedId);
    if (feature) setConfig(copy(feature.config || {}));
  }, [features, selectedId]);

  const sections = useMemo(() => {
    if (!selected) return [];
    const templateSections = (selected.template.sections || []).map((section) => ({
      ...section,
      groups: (section.groups || []).map((group) => ({ ...group, items: [...(group.items || [])] })),
      custom: false,
    }));
    const customSections = (config.custom_sections || []).map((section) => ({ ...section, custom: true, groups: [] }));
    const all = [...templateSections, ...customSections];
    for (const group of config.custom_groups || []) {
      const section = all.find((item) => item.key === group.section_key);
      if (section) section.groups.push({ ...group, custom: true, items: (config.custom_items || []).filter((item) => item.group_key === group.key) });
    }
    return all.sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || a.label.localeCompare(b.label));
  }, [selected, config]);

  function sectionValue(section, field) {
    return (section.custom ? section : config.section_overrides?.[section.key] || {})[field] ?? section[field];
  }

  function groupValue(group, field) {
    return (group.custom ? group : config.group_overrides?.[group.key] || {})[field] ?? group[field];
  }

  function updateCustomArray(name, key, field, value) {
    setConfig((current) => ({ ...current, [name]: (current[name] || []).map((item) => item.key === key ? { ...item, [field]: value } : item) }));
  }

  function updateSection(section, field, value) {
    if (section.custom) updateCustomArray("custom_sections", section.key, field, value);
    else updateMap(setConfig, "section_overrides", section.key, field, value);
  }

  function updateGroup(group, field, value) {
    if (group.custom) updateCustomArray("custom_groups", group.key, field, value);
    else updateMap(setConfig, "group_overrides", group.key, field, value);
  }

  function moveSection(section, direction) {
    const index = sections.findIndex((item) => item.key === section.key);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setConfig((current) => {
      const next = copy(current);
      for (const [position, item] of reordered.entries()) {
        const order = (position + 1) * 10;
        if (item.custom) next.custom_sections = (next.custom_sections || []).map((entry) => entry.key === item.key ? { ...entry, order } : entry);
        else next.section_overrides = { ...(next.section_overrides || {}), [item.key]: { ...(next.section_overrides?.[item.key] || {}), order } };
      }
      return next;
    });
  }

  async function save() {
    setStatus("Guardando...");
    const response = await fetch(`/api/menu/features/${encodeURIComponent(selectedId)}`, { method: "PUT", credentials: "same-origin", headers: authHeaders(), body: JSON.stringify({ config }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) { setStatus(data.error || "No se pudo guardar."); return; }
    setStatus("Configuración guardada.");
    await load();
  }

  async function restore() {
    if (!window.confirm("¿Restaurar la plantilla original de esta feature?")) return;
    const response = await fetch(`/api/menu/features/${encodeURIComponent(selectedId)}/restore`, { method: "POST", credentials: "same-origin", headers: authHeaders() });
    if (!response.ok) { setStatus("No se pudo restaurar la plantilla."); return; }
    setStatus("Plantilla original restaurada.");
    await load();
  }

  function addSection() {
    const label = window.prompt("Nombre de la nueva sección");
    if (!label) return;
    const key = `custom:section:${Date.now()}`;
    setConfig((current) => ({ ...current, custom_sections: [...(current.custom_sections || []), { key, label, icon: "solar:widget-linear", order: (sections.length + 1) * 10, visible: true }] }));
  }

  function addGroup(sectionKey) {
    const label = window.prompt("Nombre del nuevo subgrupo");
    if (!label) return;
    const key = `custom:group:${Date.now()}`;
    setConfig((current) => ({ ...current, custom_groups: [...(current.custom_groups || []), { key, section_key: sectionKey, label, icon: "solar:list-linear", order: 1000, visible: true }] }));
  }

  function addUrl(sectionKey, groupKey) {
    const label = window.prompt("Nombre visible del enlace");
    const url = label && window.prompt("URL (https://... o ruta interna no API)");
    if (!label || !url) return;
    setConfig((current) => ({ ...current, custom_items: [...(current.custom_items || []), { key: `custom:item:${Date.now()}`, section_key: sectionKey, group_key: groupKey, label, url, icon: "solar:link-linear", order: 1000, visible: true }] }));
  }

  return (
    <div className="dashboard-main-body menu-platform-editor">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-24">
        <div><h4 className="mb-8">Menú plataforma</h4><p className="text-secondary-light mb-0">Personaliza la presentación del menú sin modificar rutas, permisos ni contratos técnicos.</p></div>
        <div className="d-flex flex-wrap gap-2"><button className="btn btn-outline-primary" type="button" onClick={addSection}><Icon icon="solar:add-circle-linear" /> Sección</button><button className="btn btn-primary" type="button" onClick={save} disabled={!selected}>Guardar cambios</button><button className="btn btn-outline-danger" type="button" onClick={restore} disabled={!selected}>Restaurar original</button></div>
      </div>
      {status && <div className="alert alert-info">{status}</div>}
      <div className="card p-24 mb-20">
        <label className="form-label">Feature a configurar</label>
        <select className="form-select" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
          {features.map((feature) => <option key={feature.id} value={feature.id}>{feature.nombre || feature.id}</option>)}
        </select>
      </div>
      {selected && <div className="d-flex flex-column gap-20">
        {sections.map((section, index) => {
          const color = CARD_COLORS[index % CARD_COLORS.length];
          const visible = sectionValue(section, "visible") !== false;
          return <section className="card p-20 menu-section-card" key={section.key} style={{ backgroundColor: color.background, borderColor: color.border }}>
            <div className="d-flex flex-wrap align-items-center gap-12 mb-16">
              <Icon icon={sectionValue(section, "icon") || "solar:widget-linear"} className="text-2xl" style={{ color: color.accent }} />
              <input className="form-control menu-section-title-input" value={sectionValue(section, "label") || ""} onChange={(event) => updateSection(section, "label", event.target.value)} aria-label="Título de sección" />
              <IconPicker value={sectionValue(section, "icon")} onChange={(value) => updateSection(section, "icon", value)} />
              <label className="form-switch d-flex align-items-center gap-8 mb-0" title="Mostrar u ocultar esta sección"><input className="form-check-input" type="checkbox" checked={visible} onChange={(event) => updateSection(section, "visible", event.target.checked)} /><span>{visible ? "Visible" : "Oculta"}</span></label>
              <div className="d-flex gap-1 ms-auto">
                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => moveSection(section, -1)} disabled={index === 0} title="Subir sección" aria-label="Subir sección"><Icon icon="solar:alt-arrow-up-linear" /></button>
                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => moveSection(section, 1)} disabled={index === sections.length - 1} title="Bajar sección" aria-label="Bajar sección"><Icon icon="solar:alt-arrow-down-linear" /></button>
              </div>
            </div>
            {(section.groups || []).map((group) => {
              const groupVisible = groupValue(group, "visible") !== false;
              return <div className="border rounded p-16 mb-12 bg-white" key={group.key}>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-12">
                  <Icon icon={groupValue(group, "icon") || "solar:list-linear"} className="text-xl text-secondary-light" />
                  <input className="form-control" style={{ maxWidth: 240 }} value={groupValue(group, "label") || ""} onChange={(event) => updateGroup(group, "label", event.target.value)} aria-label="Título de subgrupo" />
                  <input className="form-control" style={{ maxWidth: 220 }} value={groupValue(group, "icon") || ""} onChange={(event) => updateGroup(group, "icon", event.target.value)} aria-label="Icono de subgrupo" />
                  <input className="form-control" type="number" style={{ maxWidth: 100 }} value={groupValue(group, "order") ?? 0} onChange={(event) => updateGroup(group, "order", event.target.value)} aria-label="Orden de subgrupo" />
                  <label className="form-switch d-flex align-items-center gap-8 mb-0"><input className="form-check-input" type="checkbox" checked={groupVisible} onChange={(event) => updateGroup(group, "visible", event.target.checked)} /><span>{groupVisible ? "Visible" : "Oculto"}</span></label>
                  <button className="btn btn-sm btn-outline-secondary ms-auto" type="button" onClick={() => addUrl(section.key, group.key)}><Icon icon="solar:add-circle-linear" /> URL</button>
                </div>
                {(group.items || []).map((item) => {
                  const override = config.item_overrides?.[item.key] || {};
                  return <div className="row align-items-center gy-2 mb-8" key={item.key}>
                    <div className="col-lg-4"><input className="form-control" value={override.label ?? item.label} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "label", event.target.value)} aria-label="Nombre de botón" /></div>
                    <div className="col-lg-3"><input className="form-control" value={override.icon ?? item.icon} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "icon", event.target.value)} aria-label="Icono de botón" /></div>
                    <div className="col-lg-2"><input className="form-control" type="number" value={override.order ?? item.order} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "order", event.target.value)} aria-label="Orden de botón" /></div>
                    <div className="col-lg-2"><label className="form-switch d-flex align-items-center gap-8"><input className="form-check-input" type="checkbox" checked={override.visible ?? item.visible !== false} onChange={(event) => updateMap(setConfig, "item_overrides", item.key, "visible", event.target.checked)} /><span>Visible</span></label></div>
                    <div className="col-lg-1 text-secondary-light" title="Ruta técnica protegida"><Icon icon="solar:lock-keyhole-linear" /></div>
                  </div>;
                })}
              </div>;
            })}
            <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => addGroup(section.key)}><Icon icon="solar:add-circle-linear" /> Subgrupo</button>
          </section>;
        })}
      </div>}
    </div>
  );
}
